/**
 * Inner layout of the Auth Letter Upload modal: sidebar plus form.
 *
 * @module authLetterUploadModalContent
 */
import { AuthLetterUploadFormState } from "../../../data-model/candidAuthorizationModel";
import AuthLetterNavSidebar from "./authLetterNavSidebar";
import AuthLetterUploadForm from "./authLetterUploadForm";

const BASE_CLASS_NAME = "auth-letter-upload-modal";

/**
 * Props for {@link AuthLetterUploadModalContent}.
 */
interface AuthLetterUploadModalContentProps {
  /** Authorization tabs shown in the sidebar. */
  formStates: AuthLetterUploadFormState[];
  /** Index of the active authorization tab. */
  selectedIndex: number;
  /** Tab indexes that cannot be selected yet. */
  disabledIndexes: Set<number>;
  /** Form snapshot for the active tab. */
  currentFormState: AuthLetterUploadFormState;
  /** Selects a sidebar tab. */
  onSelect: (index: number) => void;
  /** Removes a sidebar tab. */
  onDelete: (index: number) => void;
  /** Appends a blank authorization tab. */
  onAddMore: () => void;
  /** Updates a single field on the active tab. */
  onFormChange: <K extends keyof AuthLetterUploadFormState>(
    field: K,
    value: AuthLetterUploadFormState[K],
  ) => void;
  /** When true, "Add More" is hidden because the current tab is incomplete. */
  isSaveDisabled: boolean;
}

/**
 * Renders the two-pane Auth Letter Upload body, including a helper banner
 * that this form adds a new authorization for the current patient.
 *
 * @param props - Sidebar, form, and handler props
 * @returns The modal body layout
 */
const AuthLetterUploadModalContent = ({
  formStates,
  selectedIndex,
  disabledIndexes,
  currentFormState,
  onSelect,
  onDelete,
  onAddMore,
  onFormChange,
  isSaveDisabled,
}: AuthLetterUploadModalContentProps) => (
  <div
    className={`${BASE_CLASS_NAME} flex min-h-0 gap-2 rounded-md border border-primaryGray-14`}
  >
    <AuthLetterNavSidebar
      items={formStates}
      selectedIndex={selectedIndex}
      disabledIndexes={disabledIndexes}
      onSelect={onSelect}
      onDelete={onDelete}
      onAddMore={onAddMore}
      isSaveDisabled={isSaveDisabled}
    />
    <div
      className={`${BASE_CLASS_NAME}-right-section flex min-w-0 flex-1 flex-col gap-2 p-4`}
    >
      <div className="flex flex-col gap-2 rounded-md bg-secondaryYellow-11 p-2">
        <div className="text-xs font-medium text-primaryGray-4">
          Add new authorization for this patient
        </div>
      </div>
      <AuthLetterUploadForm
        key={selectedIndex}
        value={currentFormState}
        onChange={onFormChange}
      />
    </div>
  </div>
);

export default AuthLetterUploadModalContent;
